"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

/**
 * Satu pintu untuk SEMUA widget Turnstile di app ini.
 *
 * Kenapa ada: token Turnstile SEKALI PAKAI. Sebelum ini, submit yang ditolak
 * karena alasan NON-captcha — 409 "Email sudah terdaftar", nomor WhatsApp
 * duplikat, domain email tanpa MX, pendaftaran ditutup — tetap menghabiskan
 * token, tapi widget tidak pernah di-reset dan state token tidak dibersihkan.
 * Retry berikutnya mengirim token yang SAMA, Cloudflare membalas
 * `timeout-or-duplicate`, dan pengguna terjebak "Validasi keamanan gagal"
 * selamanya sampai halaman di-reload.
 *
 * Untuk tenant bimbel, 409 duplikat itu kejadian harian — makanya admin sering
 * kena sementara siapa pun yang menguji sekali jalan dengan data bersih tidak
 * pernah melihatnya.
 *
 * KONTRAK PEMAKAIAN: panggil `reset()` di SETIAP cabang submit yang gagal,
 * bukan hanya saat error-nya captcha. Untuk penolakan dari server, pakai
 * `noteServerRejection(errorType)` supaya pesannya ikut menyesuaikan.
 *
 * Sengaja satu hook, bukan `useRef` ditabur per halaman: pola ini sudah
 * disalin lima kali dan sudah divergen — dua tempat punya onError/onExpire
 * dengan teks berbeda, tiga tempat tidak punya sama sekali, dan bug layout
 * ikut tersalin.
 */
export type TurnstileStatus = /** Site key kosong → Turnstile mati (fail-open, sama seperti sisi server). */
"disabled"
/** Widget sedang menantang, token belum ada. */ | "loading"
/** Token siap dipakai. */ | "ready"
/** Token kedaluwarsa / habis dipakai; widget sedang menerbitkan yang baru. */ | "refreshing"
/** Widget tak bisa jalan: sitekey salah, domain belum di allow-list, adblock. */ | "blocked"
/** Gangguan sementara di sisi Cloudflare atau jaringan. */ | "unavailable";
const MESSAGES: Record<TurnstileStatus, string | null> = {
  disabled: null,
  loading: null,
  ready: null,
  refreshing: "Verifikasi keamanan diperbarui otomatis. Tunggu sebentar, lalu tekan tombolnya lagi — data yang kamu isi tidak hilang.",
  blocked: "Verifikasi keamanan tidak bisa dimuat di halaman ini. Matikan pemblokir iklan atau mode hemat data, lalu muat ulang halaman.",
  unavailable: "Layanan verifikasi keamanan sedang bermasalah. Tunggu sebentar, lalu coba lagi."
};

/** errorType dari API (apps/api/src/lib/turnstile.ts) → status hook. */
const SERVER_ERROR_STATUS: Record<string, TurnstileStatus> = {
  TURNSTILE_MISSING: "refreshing",
  TURNSTILE_EXPIRED: "refreshing",
  TURNSTILE_INVALID: "blocked",
  TURNSTILE_MISCONFIGURED: "unavailable",
  TURNSTILE_UNAVAILABLE: "unavailable"
};

/** true untuk errorType apa pun yang berasal dari gerbang Turnstile. */
export function isTurnstileError(errorType: unknown): errorType is string {
  return typeof errorType === "string" && errorType.startsWith("TURNSTILE_");
}
export function useTurnstile(action) {
  const $ = _c(10);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const ref = useRef(undefined);
  const [token, setToken] = useState("");
  const [status, setStatus] = useState(siteKey ? "loading" : "disabled");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!siteKey) {
        return;
      }
      setToken("");
      setStatus("refreshing");
      ref.current?.reset();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const reset = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = errorType => {
      const next = errorType && SERVER_ERROR_STATUS[errorType] || "refreshing";
      setToken("");
      setStatus(next);
      if (next === "refreshing") {
        ref.current?.reset();
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const noteServerRejection = t1;
  const message = MESSAGES[status];
  let t2;
  if ($[2] !== action || $[3] !== message) {
    t2 = siteKey ? <div className="pt-2"><div className="flex justify-center"><Turnstile ref={ref} siteKey={siteKey} options={{
          action,
          refreshExpired: "auto",
          refreshTimeout: "auto",
          retry: "auto"
        }} onSuccess={t => {
          setToken(t);
          setStatus("ready");
        }} onExpire={() => {
          setToken("");
          setStatus("refreshing");
        }} onTimeout={() => {
          setToken("");
          setStatus("refreshing");
          ref.current?.reset();
        }} onError={code => {
          setToken("");
          setStatus(String(code ?? "").startsWith("110") ? "blocked" : "unavailable");
        }} /></div>{message && <p role="status" aria-live="polite" className="mt-2 text-center text-xs text-amber-700">{message}</p>}</div> : null;
    $[2] = action;
    $[3] = message;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const widget = t2;
  const t3 = !!siteKey && !token;
  let t4;
  if ($[5] !== status || $[6] !== t3 || $[7] !== token || $[8] !== widget) {
    t4 = {
      token,
      required: !!siteKey,
      pending: t3,
      status,
      reset,
      noteServerRejection,
      widget
    };
    $[5] = status;
    $[6] = t3;
    $[7] = token;
    $[8] = widget;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}